import { useEffect } from 'react';
import invoiceService from '../../../services/invoiceService';
import quickbooksService from '../../../services/quickbooksService';
import authService from '../../../services/authService';
import customerService from '../../../services/customerService';
import useImportStream from '../../../hooks/useImportStream';
import { getFranchiseeId } from '../services/invoiceHelpers';
import { defaultFilters, getDateRangeFromPreset } from '../utils/invoiceConstants';

export const useInvoiceActions = (invoiceState, userProfile, notifications, fetchInvoices, onCustomerSelect) => {
  const {
    setInvoices,
    setError,
    setCurrentPage,
    setFilters,
    setImporting,
    setImportProgress,
    setTotalInvoices,
    setTotalPages,
    setHasNewRecords,
    currentPage,
    searchTerm,
    filters,
    invoices
  } = invoiceState;

  const { showNotification } = notifications;

  // Initialize SSE hook for real-time updates
  const {
    isConnected: isSSEConnected,
    connectionError: sseError,
    importStatus,
    newRecords,
    importProgress: sseProgress,
    connectToImportStream,
    disconnectFromImportStream,
    clearNewRecords,
    isImporting: isSSEImporting,
    isCompleted: isSSECompleted
  } = useImportStream();

  // Handle SSE import completion
  useEffect(() => {
    if (isSSECompleted) {
      handleImportComplete();
    }
  }, [isSSECompleted]);

  // Handle SSE errors
  useEffect(() => {
    if (sseError) {
      console.error('SSE connection error:', sseError);
      setError(`Connection error: ${sseError}`);
    }
  }, [sseError]);

  // Clear new records when page changes or search/filters change
  useEffect(() => {
    if (newRecords.length > 0) {
      clearNewRecords();
    }
    setHasNewRecords(false);
  }, [currentPage, searchTerm, filters]);

  const handleSearch = (value) => {
    invoiceState.setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => {
      const updated = { ...prev, [filterKey]: value };
      // When selecting a preset, populate date fields so they're visible and editable
      if (filterKey === 'datePreset' && value && value !== 'custom') {
        const { from, to } = getDateRangeFromPreset(value, '', '');
        updated.dateFrom = from;
        updated.dateTo = to;
      }
      // When user manually changes a date, switch preset to custom
      if (filterKey === 'dateFrom' || filterKey === 'dateTo') {
        updated.datePreset = 'custom';
      }
      return updated;
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setCurrentPage(1);
  };

  // Handle real-time invoice updates (Page 1 only)
  const handleNewInvoices = (newInvoices, progressData) => {
    if (currentPage === 1 && !searchTerm && !Object.values(filters).some(f => f)) {
      // Only add to Page 1 and when not searching/filtering
      console.log('📊 Adding new invoices to list:', newInvoices);
      
      setInvoices(prev => {
        // Add new invoices to the beginning (invoice_date DESC order)
        const existingIds = new Set(prev.map(i => i.id));
        const uniqueNewInvoices = newInvoices.filter(i => !existingIds.has(i.id));
        
        if (uniqueNewInvoices.length > 0) {
          // Update total count
          setTotalInvoices(count => count + uniqueNewInvoices.length);
          
          return [...uniqueNewInvoices, ...prev].slice(0, 50); // Keep only 50 items per page
        }
        return prev;
      });
    } else {
      // Show notification for other pages
      setHasNewRecords(true);
    }
  };

  // Handle SSE import completion
  const handleImportComplete = () => {
    setImporting(false);
    setImportProgress({ current: 0, total: 0 });
    
    if (currentPage !== 1 || searchTerm || Object.values(filters).some(f => f)) {
      // If not on page 1 or searching/filtering, show notification to refresh
      setHasNewRecords(true);
      showNotification('Import completed! Refresh to see all new invoices.', 'success');
    } else {
      // Always refresh the invoice list after import to ensure all data is current
      fetchInvoices();
      showNotification('Import completed! Invoice list updated.', 'success');
    }
  };

  const handleImportFromQuickBooks = async () => {
    const franchiseeId = getFranchiseeId(userProfile);
    
    if (!franchiseeId) {
      setError('No franchisee ID found');
      return;
    }

    setImporting(true);
    setHasNewRecords(false);
    
    try {
      console.log('📡 Starting QuickBooks invoice import with real-time updates...');
      
      // Connect to SSE stream for real-time updates
      connectToImportStream(
        franchiseeId, 
        'invoices',
        // Progress callback (backward compatibility)
        (current, total) => {
          setImportProgress({ current, total });
          if (total === 1 && current === 0) {
            console.log('Getting QuickBooks invoice count...');
          } else if (total > 1) {
            console.log(`Processing ${current}/${total} invoices...`);
          }
        },
        // New records callback (real-time updates)
        handleNewInvoices
      );
      
      // Give SSE connection a moment to establish
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Start the import process
      const importResult = await invoiceService.importFromQuickBooks(franchiseeId);
      console.log('📡 Import API call completed:', importResult);
      
    } catch (error) {
      console.error('Error importing invoices:', error);
      setError(`Failed to import invoices: ${error.message}`);
      setImporting(false);
      setImportProgress({ current: 0, total: 0 });
      disconnectFromImportStream();
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const franchiseeId = invoice.franchisee_id || getFranchiseeId(userProfile);
      if (!franchiseeId) {
        setError('No franchisee ID found');
        return;
      }

      // Fetch detailed invoice data with line items
      const detailedInvoice = await invoiceService.getInvoice(franchiseeId, invoice.id);
      // Carry over resolved technician_name from list data (resolved by branchMappingService, not in single-invoice response)
      if (invoice.technician_name && !detailedInvoice.technician_name) {
        detailedInvoice.technician_name = invoice.technician_name;
      }
      invoiceState.setSelectedInvoice(detailedInvoice);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setError(`Failed to load invoice details: ${error.message}`);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    // Use the invoice's own franchisee_id for the API call (admin gets 'all' from getFranchiseeId which won't match)
    const franchiseeId = invoice.franchisee_id || getFranchiseeId(userProfile);

    if (!franchiseeId) {
      setError('No franchisee ID found');
      return;
    }

    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This action cannot be undone.`)) {
      return;
    }

    try {
      await invoiceService.deleteInvoice(franchiseeId, invoice.id);
      
      // Remove invoice from the list in real-time
      setInvoices(prevInvoices => prevInvoices.filter(inv => inv.id !== invoice.id));
      
      // Update total count
      setTotalInvoices(prevTotal => prevTotal - 1);
      
      // Show success message
      showNotification(`Invoice ${invoice.invoice_number} deleted successfully`, 'success');
      
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setError(`Failed to delete invoice: ${error.message}`);
    }
  };

  const handleViewPDF = async (invoice) => {
    const franchiseeId = invoice.franchisee_id || getFranchiseeId(userProfile);
    if (!franchiseeId) {
      setError('No franchisee ID found');
      return;
    }
    try {
      await invoiceService.viewPDF(franchiseeId, invoice.id);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      setError(`Failed to view PDF: ${error.message}`);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    const franchiseeId = invoice.franchisee_id || getFranchiseeId(userProfile);

    if (!franchiseeId) {
      setError('No franchisee ID found');
      return;
    }

    try {
      // Use local PDF generation for all invoices
      await invoiceService.downloadPDF(franchiseeId, invoice.id, invoice.invoice_number);
      showNotification(`PDF download started for invoice ${invoice.invoice_number}`, 'success');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError(`Failed to download PDF: ${error.message}`);
    }
  };

  const handleDeleteAllInvoices = async () => {
    // Only allow if admin is impersonating (same logic as customers)
    if (!authService.hasAdminReturn() || userProfile?.role === 'admin') {
      setError('Only admin impersonating franchisee can delete all invoices');
      return;
    }
    
    const franchiseeId = getFranchiseeId(userProfile);
    
    if (!franchiseeId) {
      setError('No franchisee context found');
      return;
    }

    const confirmText = `DELETE ALL INVOICES`;
    const userInput = window.prompt(
      `⚠️ WARNING: This will permanently delete ALL invoices for this franchisee!\n\n` +
      `This action cannot be undone and will remove all invoice data.\n\n` +
      `To confirm, type exactly: ${confirmText}`
    );

    if (userInput !== confirmText) {
      setError('Deletion cancelled - confirmation text did not match');
      return;
    }

    try {
      const result = await invoiceService.deleteAllInvoices(franchiseeId);
      
      // Clear the invoice list in real-time
      setInvoices([]);
      setTotalInvoices(0);
      setTotalPages(1);
      setCurrentPage(1);
      
      // Show success message
      showNotification(`Successfully deleted ${result.deleted} invoices`, 'success');
      
    } catch (error) {
      console.error('Error deleting all invoices:', error);
      setError(`Failed to delete invoices: ${error.message}`);
    }
  };

  const handleViewCustomer = async (invoice) => {
    if (!invoice.customer_ref && !invoice.customer_id) {
      setError('No customer reference found for this invoice');
      return;
    }

    const franchiseeId = invoice.franchisee_id || getFranchiseeId(userProfile);
    if (!franchiseeId) {
      setError('No franchisee ID found');
      return;
    }

    if (!onCustomerSelect) {
      setError('Customer selection not available');
      return;
    }

    try {
      let customer = null;

      // Try QB customer ID lookup first
      if (invoice.customer_ref) {
        const customerResponse = await customerService.getCustomerByQbId(franchiseeId, invoice.customer_ref);
        if (customerResponse?.customer) {
          customer = customerResponse.customer;
        }
      }

      // Fallback: try direct customer ID lookup
      if (!customer && invoice.customer_id) {
        const customerResponse = await customerService.getCustomer(franchiseeId, invoice.customer_id);
        if (customerResponse?.customer) {
          customer = customerResponse.customer;
        }
      }

      if (!customer) {
        alert(`Customer Information:\n\nName: ${invoice.customer_name}\nEmail: ${invoice.customer_email || 'Not available'}\n\nNote: Customer details page not available - customer may need to be imported.`);
        return;
      }

      onCustomerSelect(customer);
    } catch (error) {
      console.error('Error finding customer:', error);
      setError(`Failed to find customer: ${error.message}`);
    }
  };

  return {
    handleSearch,
    handleFilterChange,
    clearFilters,
    handleImportFromQuickBooks,
    handleViewInvoice,
    handleDeleteInvoice,
    handleViewPDF,
    handleDownloadPDF,
    handleDeleteAllInvoices,
    handleViewCustomer,
    // SSE related
    isSSEImporting,
    sseProgress,
    isSSEConnected
  };
};